import axios, { AxiosError, AxiosPromise } from "axios";

import { AppointmentModel, parseAppointmentModel } from "@teamprevent/common/appointment";
import Utils from "@teamprevent/common/utils";
import { CaseType } from "@teamprevent/common/model";
import { LinkObject } from "@teamprevent/common/model";

import { OhCaseListModel, OhCaseDetailModel, OhCaseCreateModel, OhCaseEditModel, parseModel } from "./model";

class OhCaseApi {

    private readonly apiUrl = "/api/case";

    public async getAll(filters?: {
        status?: string; caseId?: string; employeeFirstName?: string; employeeLastName?: string; include?: string; secondaryStatus?: string;
    }): Promise<OhCaseListModel[]> {
        const response = await axios.get(this.apiUrl, { params: filters });
        const data = response.data as OhCaseListModel[];
        return data.map(parseModel);
    }

    public async getOpenCount(include?: CaseType): Promise<number> {
        return (await axios.get(`${this.apiUrl}/opencount`, { params: { include } })).data;
    }

    public async detail(id: any): Promise<OhCaseDetailModel> {
        try {
            const query = "?parameters=" + Utils.getParameters();
            const response = await axios.get(`${this.apiUrl}/${id}${query}`);
            const model = response.data as OhCaseDetailModel;
            return parseModel(model);
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError?.response?.status === 400) {
                const error = { message: axiosError?.response?.data, status: 400 };
                throw error;
            }

            throw ex;
        }
    }

    public async getAppointments(ohCaseId: string): Promise<AppointmentModel[]> {
        const response = await axios.get<AppointmentModel[]>(`${this.apiUrl}/${ohCaseId}/appointments`);
        return response.data.map(parseAppointmentModel);
    }

    public referralDetail(id: any): AxiosPromise {
        const query = "?parameters=" + Utils.getParameters();
        return axios.get(`${this.apiUrl}/${id}/matchedreferral/${query}`);
    }

    public async save(ohCase: OhCaseEditModel): Promise<OhCaseDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.put(`${this.apiUrl}/${ohCase.id}${query}`, ohCase);
        const model = response.data as OhCaseDetailModel;
        return parseModel(model);
    }

    public async create(ohCase: OhCaseCreateModel): Promise<OhCaseDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.post(`${this.apiUrl}${query}`, ohCase);
        const model = response.data as OhCaseDetailModel;
        return parseModel(model);
    }

    public delete(id: any): Promise<void> {
        return axios.delete(`${this.apiUrl}/${id}`);
    }

    public cancel(id: string, notes: string): Promise<void> {
        return axios.post(`${this.apiUrl}/${id}/cancel`, { notes });
    }

    public async updateAssociatedManagers(ohCaseId: string, associatedManagers: LinkObject[], associatedHrUsers: LinkObject[]): Promise<void> {
        await axios.post(`${this.apiUrl}/${ohCaseId}/additionalManagers`, { associatedManagers, associatedHrUsers });
    }
}

export default new OhCaseApi();
